import { SOLVE_AUTOFLOW_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import { getEditingSavedVersion } from '../editingSavedVersion';
import { getMCPToolName } from 'src/pages/workflow-builder-edit/autonomous-agent/action-drawer/helpers';
import { mergeChangedHunks } from 'src/pages/workflow-builder-edit/autonomous-agent/insights/inline-review/mergeChangedHunks';
import {
  getImprovedPolicyGenerationError,
  isEligibilityResult,
} from 'src/pages/workflow-builder-edit/autonomous-agent/insights/on-demand-generation/helpers';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { SolveWidgetProduct } from 'src/types/types';
import {
  AddAutoflowToolRequest,
  AutoflowIntentWorkflowResponse,
  AutoflowToolResponse,
  AutoflowVersionHistory,
  AutoflowVersionHistorySummary,
  BuiltInToolSchemasResponse,
  ContextAttachmentResponse,
  CustomEventCustomizationSettings,
  CustomEventToolConfigurationFields,
  DeleteAutoflowToolRequest,
  DeletedBuildingBlockResponse,
  ImprovedPolicyGenerationEligibility,
  ImprovedPolicyGenerationJob,
  PatchAutoflowCVRequest,
  PatchAutoflowVersionHistoryRequest,
  PolicyDiffCreateRequest,
  PolicyDiffResponse,
  PolicyDiffSaveProgressRequest,
  PolicyDiffSaveProgressResponse,
  RestorationRequirements,
  TemplateAutoflowIntentWorkflow,
  TriggerImprovedPolicyGenerationResponse,
} from 'src/types/workflowBuilderAPITypes';

export const workflowBuilderAutoflowApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    addAutoflowTool: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: AddAutoflowToolRequest;
        intentWorkflowId: string;
        version?: number;
        versionType?: WorkflowVersionType;
      }
    >({
      invalidatesTags: [
        'AutoflowVersionHistorySummary',
        'AutoflowWorkflow',
        'Actions',
        'IntentUsages',
        'ActionUsages',
        'CustomEventConfigurationUsages',
      ],
      query: ({ body, intentWorkflowId, version, versionType }) => ({
        body,
        method: 'POST',
        params:
          version !== undefined || versionType
            ? {
                ...(version !== undefined && { version }),
                ...(versionType && { version_type: versionType }),
              }
            : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/tool`,
      }),
    }),
    changeWorkflowPublishStatus: builder.mutation<
      AutoflowIntentWorkflowResponse,
      { intentWorkflowId: string; status: 'enabled' | 'disabled' }
    >({
      invalidatesTags: ['AutoflowWorkflow', 'Tools', 'Intents', 'Workflow'],
      query: ({ intentWorkflowId, status }) => ({
        method: 'POST',
        params: { status },
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/change-publish-status`,
      }),
    }),
    clonePrivateWorkflows: builder.mutation<
      {
        cloned_intents: Array<{
          new_intent_id: string;
          new_intent_name: string;
          new_intent_workflow_id: string;
          source_intent_id: string;
        }>;
      },
      {
        body: {
          parent_autoflow_intent_workflow_id: string;
          source_intent_ids: string[];
          source_product: SolveWidgetProduct;
        };
      }
    >({
      invalidatesTags: ['AutoflowWorkflow', 'Tools', 'Intents', 'IntentUsages'],
      query: ({ body }) => ({
        body: {
          ...body,
          editing_saved_version: getEditingSavedVersion(),
        },
        method: 'POST',
        url: SOLVE_AUTOFLOW_BASE_URL + '/workflow-builder/clone-workflows',
      }),
    }),
    createBlankSavedVersion: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: {
          description?: string;
          source_recommendation_id?: string;
          version_name: string;
        };
        intentWorkflowId: string;
      }
    >({
      invalidatesTags: [
        'AutoflowWorkflow',
        'AutoflowVersionHistorySummary',
        'IntentUsages',
        'Intents',
      ],
      query: ({ body, intentWorkflowId }) => ({
        body,
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/v2/versions/save-blank`,
      }),
    }),
    createPolicyDiff: builder.mutation<
      PolicyDiffResponse,
      { body: PolicyDiffCreateRequest; editSessionUuid?: string }
    >({
      // Creating a review forks a SAVED version and actions the recommendation,
      // so refresh the version list and the improved-policy surfaces.
      invalidatesTags: [
        'AutoflowVersionHistorySummary',
        'AutoflowWorkflow',
        'IntentUsages',
        'Intents',
      ],
      query: ({ body, editSessionUuid }) => ({
        body,
        // Stamp the originating edit session so the BE echoes it on the Pusher
        // event and this tab can ignore its own change (see PusherEventHooks).
        headers: editSessionUuid
          ? { 'edit-session-uuid': editSessionUuid }
          : undefined,
        method: 'POST',
        url: SOLVE_AUTOFLOW_BASE_URL + '/policy-diff/create',
      }),
    }),
    createSavedVersion: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: {
          description?: string;
          version: number | null;
          version_name: string;
          version_type: string;
        };
        intentWorkflowId: string;
      }
    >({
      invalidatesTags: [
        'AutoflowWorkflow',
        'AutoflowVersionHistorySummary',
        'IntentUsages',
        'Tools',
      ],
      query: ({ body, intentWorkflowId }) => ({
        body,
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/save`,
      }),
    }),
    customizeAutoflowTemplate: builder.mutation<
      AutoflowIntentWorkflowResponse,
      string
    >({
      invalidatesTags: ['Intents'],
      query: intentWorkflowId => ({
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL + `/templates/${intentWorkflowId}/customize`,
      }),
    }),
    deleteAutoflowTool: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: DeleteAutoflowToolRequest;
        intentWorkflowId: string;
        version?: number;
        versionType?: WorkflowVersionType;
      }
    >({
      invalidatesTags: [
        'AutoflowVersionHistorySummary',
        'AutoflowWorkflow',
        'Actions',
        'IntentUsages',
        'ActionUsages',
        'CustomEventConfigurationUsages',
      ],
      query: ({ body, intentWorkflowId, version, versionType }) => ({
        body,
        method: 'DELETE',
        params:
          version !== undefined || versionType
            ? {
                ...(version !== undefined && { version }),
                ...(versionType && { version_type: versionType }),
              }
            : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/tool`,
      }),
    }),
    deleteVersion: builder.mutation<
      void,
      {
        intentWorkflowId: string;
        version: number;
      }
    >({
      // Deleting a version that carried a recommendation un-actions it, so the
      // improved-policy reads must refetch or the detail page keeps serving a
      // stale "no improved policy" while the list still shows the badge.
      invalidatesTags: [
        'AutoflowWorkflow',
        'AutoflowVersionHistorySummary',
        'IntentUsages',
      ],
      query: ({ intentWorkflowId, version }) => ({
        method: 'DELETE',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/${version}`,
      }),
    }),
    discardAutoflowWorkflowChanges: builder.mutation<
      AutoflowIntentWorkflowResponse,
      string
    >({
      invalidatesTags: ['AutoflowWorkflow', 'Workflow'],
      query: intentWorkflowId => ({
        method: 'DELETE',
        url: SOLVE_AUTOFLOW_BASE_URL + `/workflow-builder/${intentWorkflowId}`,
      }),
    }),
    generateImprovedPolicy: builder.mutation<
      TriggerImprovedPolicyGenerationResponse,
      {
        additionalContext?: string;
        fileIds?: string[];
        intentWorkflowId: string;
      }
    >({
      // The BE forbids auto-retrying its 503s, and a doubled POST could
      // double-trigger across the concurrency window — opt out of the global
      // one-retry wrapper.
      extraOptions: { maxRetries: 0 },
      // On 202 both reads are stale: a triggered row now exists and
      // eligibility flips to job_in_flight. On error, refresh only what the
      // specific error implies (onQueryStarted below) — a blanket
      // invalidation here would also run on failures.
      invalidatesTags: (_result, error, { intentWorkflowId }) =>
        error
          ? []
          : [
              {
                id: intentWorkflowId,
                type: 'ImprovedPolicyGenerationEligibility',
              },
              {
                id: intentWorkflowId,
                type: 'ImprovedPolicyGenerationLatestJob',
              },
            ],
      async onQueryStarted({ intentWorkflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (rejected) {
          const body = getImprovedPolicyGenerationError(
            (rejected as { error?: unknown }).error,
          );
          const eligibilityTag = {
            id: intentWorkflowId,
            type: 'ImprovedPolicyGenerationEligibility' as const,
          };
          const latestJobTag = {
            id: intentWorkflowId,
            type: 'ImprovedPolicyGenerationLatestJob' as const,
          };
          switch (body?.payload.error_code) {
            case 'eligibility_failed': {
              const embedded = body.payload.eligibility;
              if (isEligibilityResult(embedded)) {
                // The 400 already carries the server's re-check verdict —
                // seed the cache with it (zero extra requests) so the card
                // renders the exact reasons that rejected the click. The
                // reasons can include job_in_flight, so refresh the job row.
                dispatch(
                  workflowBuilderAutoflowApi.util.upsertQueryData(
                    'getImprovedPolicyGenerationEligibility',
                    intentWorkflowId,
                    embedded,
                  ),
                );
                dispatch(
                  workflowBuilderAutoflowApi.util.invalidateTags([
                    latestJobTag,
                  ]),
                );
              } else {
                dispatch(
                  workflowBuilderAutoflowApi.util.invalidateTags([
                    eligibilityTag,
                    latestJobTag,
                  ]),
                );
              }
              break;
            }
            case 'no_published_version':
              // Refetch rather than synthesize from the error payload — the
              // fresh response is the authoritative eligibility answer.
              dispatch(
                workflowBuilderAutoflowApi.util.invalidateTags([
                  eligibilityTag,
                ]),
              );
              break;
            case 'run_in_flight':
              // Someone else's run got there first; the job card takes over.
              dispatch(
                workflowBuilderAutoflowApi.util.invalidateTags([
                  eligibilityTag,
                  latestJobTag,
                ]),
              );
              break;
            case 'feature_disabled':
              // The org flag was switched off mid-session. Refreshing
              // org-config drops the flag from feature_flags, which flips the
              // gate hook false and unmounts the whole card — no point
              // refreshing the generation reads it would then skip.
              dispatch(
                workflowBuilderAutoflowApi.util.invalidateTags(['OrgConfig']),
              );
              break;
            default:
              // 503s / unknown bodies: server state didn't change — nothing
              // to refresh. The caller surfaces a toast.
              break;
          }
        }
      },
      query: ({ additionalContext, fileIds, intentWorkflowId }) => ({
        body: {
          additional_context: additionalContext ?? null,
          file_ids: fileIds ?? [],
        },
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/improved-policy/${intentWorkflowId}/generate`,
      }),
    }),
    getAllAutoflowVersionHistoriesV2: builder.query<
      Record<string, AutoflowVersionHistorySummary[]>,
      void
    >({
      providesTags: ['AutoflowVersionHistorySummary'],
      query: () => ({
        method: 'GET',
        url: SOLVE_AUTOFLOW_BASE_URL + '/workflow-builder/v2/version-histories',
      }),
    }),
    getAutoflowTemplates: builder.query<TemplateAutoflowIntentWorkflow[], void>(
      {
        query: () => ({
          method: 'GET',
          url: SOLVE_AUTOFLOW_BASE_URL + '/templates',
        }),
      },
    ),
    getAutoflowTools: builder.query<
      AutoflowToolResponse,
      { product: SolveWidgetProduct }
    >({
      providesTags: ['Tools'],
      query: ({ product }) => ({
        method: 'GET',
        params: { product },
        url: SOLVE_AUTOFLOW_BASE_URL + '/workflow-builder/tools',
      }),
      transformResponse: (response: AutoflowToolResponse) => {
        const tools = response.tools.map(tool => {
          if (tool.tool_type === 'mcp') {
            return {
              ...tool,
              tool_name: getMCPToolName(tool.tool_name),
            };
          }
          return tool;
        });
        return { ...response, tools };
      },
    }),
    getAutoflowVersionHistory: builder.query<AutoflowVersionHistory[], string>({
      providesTags: ['AutoflowWorkflow'],
      query: intentWorkflowId => ({
        method: 'GET',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/version-history`,
      }),
    }),
    getAutoflowVersionHistoryV2: builder.query<
      AutoflowVersionHistorySummary[],
      string
    >({
      keepUnusedDataFor: 300,
      providesTags: ['AutoflowVersionHistorySummary'],
      query: intentWorkflowId => ({
        method: 'GET',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/v2/version-history`,
      }),
    }),
    getAutoflowWorkflow: builder.query<AutoflowIntentWorkflowResponse, string>({
      // Carries `available_recommendation` (the improved-policy entry point), so
      // the AutoflowWorkflow tag — already invalidated by every
      // recommendation-mutating action (createPolicyDiff, save-blank, publish,
      // delete) — also refreshes the review banner + Improved policy card.
      providesTags: ['AutoflowWorkflow'],
      query: intentWorkflowId => ({
        method: 'GET',
        url: SOLVE_AUTOFLOW_BASE_URL + `/workflow-builder/${intentWorkflowId}`,
      }),
    }),
    getAutoflowWorkflowVersion: builder.query<
      AutoflowIntentWorkflowResponse,
      {
        intentWorkflowId: string;
        version: number;
        versionType?: WorkflowVersionType;
      }
    >({
      providesTags: (
        _result,
        _error,
        { intentWorkflowId, version, versionType },
      ) => [
        'AutoflowWorkflow',
        {
          id: `${intentWorkflowId}-${version}-${versionType}`,
          type: 'AutoflowWorkflow',
        },
      ],
      query: ({ intentWorkflowId, version, versionType }) => ({
        method: 'GET',
        params: versionType ? { version_type: versionType } : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/${version}`,
      }),
    }),
    getBuiltInToolSchemas: builder.query<
      BuiltInToolSchemasResponse,
      { product: SolveWidgetProduct }
    >({
      query: ({ product }) => ({
        method: 'GET',
        params: { product },
        url:
          SOLVE_AUTOFLOW_BASE_URL + '/workflow-builder/tools/built-in/schemas',
      }),
    }),
    getDeletedBuildingBlock: builder.query<
      DeletedBuildingBlockResponse,
      { intentDefinitionId: string; product?: SolveWidgetProduct }
    >({
      query: ({ intentDefinitionId, product }) => ({
        method: 'GET',
        params: product ? { product } : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/deleted-building-block/${intentDefinitionId}`,
      }),
    }),
    getImprovedPolicyGenerationEligibility: builder.query<
      ImprovedPolicyGenerationEligibility,
      string
    >({
      // A 503 means a dependency hiccuped mid-check; the BE forbids
      // hammering it, so opt out of the global one-retry wrapper. The card
      // renders a manual retry instead.
      extraOptions: { maxRetries: 0 },
      providesTags: (_result, _error, intentWorkflowId) => [
        { id: intentWorkflowId, type: 'ImprovedPolicyGenerationEligibility' },
      ],
      query: intentWorkflowId => ({
        method: 'GET',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/improved-policy/${intentWorkflowId}/eligibility`,
      }),
    }),
    getImprovedPolicyGenerationLatestJob: builder.query<
      ImprovedPolicyGenerationJob | null,
      string
    >({
      extraOptions: { maxRetries: 0 },
      providesTags: (_result, _error, intentWorkflowId) => [
        { id: intentWorkflowId, type: 'ImprovedPolicyGenerationLatestJob' },
      ],
      query: intentWorkflowId => ({
        method: 'GET',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/improved-policy/${intentWorkflowId}/latest-job`,
      }),
    }),
    getPolicyDiffByAutoflow: builder.query<PolicyDiffResponse, string>({
      providesTags: ['PolicyDiff'],
      query: autoflowId => ({
        method: 'GET',
        url: SOLVE_AUTOFLOW_BASE_URL + `/policy-diff/by-autoflow/${autoflowId}`,
      }),
    }),
    getRestorationRequirements: builder.query<
      RestorationRequirements,
      {
        intentWorkflowId: string;
        version: number;
        versionType: WorkflowVersionType;
      }
    >({
      query: ({ intentWorkflowId, version, versionType }) => ({
        method: 'GET',
        params: { version, version_type: versionType },
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/restoration-requirements`,
      }),
    }),
    getRestorationRequirementsBatch: builder.query<
      Record<string, boolean>,
      {
        intentWorkflowId: string;
        versions: Array<{ version: number; versionType: WorkflowVersionType }>;
      }
    >({
      keepUnusedDataFor: 300,
      queryFn: async (
        { intentWorkflowId, versions },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const results = await Promise.all(
          versions.map(async ({ version, versionType }) => {
            const key = `published-${version}`;
            try {
              const result = await fetchWithBQ({
                method: 'GET',
                params: { version, version_type: versionType },
                url:
                  SOLVE_AUTOFLOW_BASE_URL +
                  `/workflow-builder/${intentWorkflowId}/versions/restoration-requirements`,
              });
              if (result.error) {
                return { key, needsRestoration: true };
              }
              return {
                key,
                needsRestoration: (result.data as RestorationRequirements)
                  .needs_restoration,
              };
            } catch {
              return { key, needsRestoration: true };
            }
          }),
        );

        const restorationMap: Record<string, boolean> = {};
        results.forEach(({ key, needsRestoration }) => {
          restorationMap[key] = needsRestoration;
        });

        return { data: restorationMap };
      },
    }),
    patchAutoflowCVs: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: PatchAutoflowCVRequest;
        intentWorkflowId: string;
        version?: number;
        versionType?: WorkflowVersionType;
      }
    >({
      // 'Workflow' is intentionally broad: changing the autoflow's CV list affects
      // which CVs are considered "defined" in all child building block workflows.
      // This triggers a refetch of loadDraftWorkflow for each building block so
      // their undefined_context_variables_in_step is recomputed by the backend.
      invalidatesTags: [
        'AutoflowVersionHistorySummary',
        'AutoflowWorkflow',
        'Workflow',
      ],
      query: ({ body, intentWorkflowId, version, versionType }) => ({
        body: body,
        method: 'PATCH',
        params:
          version !== undefined || versionType
            ? {
                ...(version !== undefined && { version }),
                ...(versionType && { version_type: versionType }),
              }
            : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/context-variable`,
      }),
    }),
    patchVersionHistory: builder.mutation<
      AutoflowVersionHistory[],
      {
        body: PatchAutoflowVersionHistoryRequest;
        intentWorkflowId: string;
        version: number;
      }
    >({
      invalidatesTags: ['AutoflowWorkflow'],
      query: ({ body, intentWorkflowId, version }) => ({
        body: body,
        method: 'PATCH',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/version-history/${version}`,
      }),
    }),
    publishLatestVersion: builder.mutation<
      AutoflowIntentWorkflowResponse,
      { intentWorkflowId: string }
    >({
      // Publishing changes what generation eligibility is computed against
      // (no_published_version / empty_policy / workflow_version).
      invalidatesTags: [
        'AutoflowWorkflow',
        'AutoflowVersionHistorySummary',
        'ImprovedPolicyGenerationEligibility',
        'IntentUsages',
        'Intents',
        'Workflow',
      ],
      query: ({ intentWorkflowId }) => ({
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/publish-latest`,
      }),
    }),
    publishWorkflowVersion: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: {
          version: number;
          version_description?: string;
          version_type: WorkflowVersionType;
        };
        intentWorkflowId: string;
      }
    >({
      // Publishing changes what generation eligibility is computed against
      // (no_published_version / empty_policy / workflow_version).
      invalidatesTags: [
        'Actions',
        'AutoflowWorkflow',
        'AutoflowVersionHistorySummary',
        'ImprovedPolicyGenerationEligibility',
        'IntentUsages',
        'Workflow',
      ],
      query: ({ body, intentWorkflowId }) => ({
        body,
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/publish`,
      }),
    }),
    resetPolicyDiff: builder.mutation<
      PolicyDiffResponse,
      { autoflowId: string; editSessionUuid?: string }
    >({
      // Reset reverts the linked version's committed content to the seeded
      // skeleton and returns every hunk to pending, so the cached PolicyDiff is
      // stale. The caller re-seeds the overlay from the response directly; the
      // tag invalidation keeps a later by-autoflow read consistent.
      invalidatesTags: ['PolicyDiff'],
      query: ({ autoflowId, editSessionUuid }) => ({
        // See createPolicyDiff: lets this tab ignore its own Pusher echo.
        headers: editSessionUuid
          ? { 'edit-session-uuid': editSessionUuid }
          : undefined,
        method: 'POST',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/policy-diff/by-autoflow/${autoflowId}/reset`,
      }),
    }),
    savePolicyDiffProgress: builder.mutation<
      PolicyDiffSaveProgressResponse,
      {
        autoflowId: string;
        body: PolicyDiffSaveProgressRequest;
        editSessionUuid?: string;
      }
    >({
      // No tag invalidation: this is review-mode autosave, and refetching
      // by-autoflow on every flush would thrash. Instead, write the returned
      // review state straight into the by-autoflow cache so the originating tab
      // stays coherent without a round-trip. Without this the cached PolicyDiff
      // goes stale and a client-side nav back to the version re-seeds the inline
      // review from the pre-edit snapshot (only a hard reload, which wipes the
      // store, would heal it). The originating tab ignores its own Pusher echo
      // (edit-session-uuid), so it can't lean on VERSION_CONTENT_CHANGE for this;
      // that event still drives the refresh for OTHER tabs.
      async onQueryStarted({ autoflowId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Merge the delta into the cached full review (see mergeChangedHunks),
          // keeping the cached snapshot. updateQueryData no-ops when nothing is
          // cached (a prior GET errored or aged out); a reload re-seeds from the
          // full GET in that case.
          dispatch(
            workflowBuilderAutoflowApi.util.updateQueryData(
              'getPolicyDiffByAutoflow',
              autoflowId,
              draft => {
                draft.hunks = mergeChangedHunks(
                  draft.hunks,
                  data.changed_hunks,
                );
                draft.has_pending_hunks = data.has_pending_hunks;
              },
            ),
          );
        } catch {
          // Autosave failed: flushHunkOps surfaces a retry toast and a reload
          // re-injects from server truth, so leave the cache untouched here.
        }
      },
      query: ({ autoflowId, body, editSessionUuid }) => ({
        body,
        // See createPolicyDiff: lets this tab ignore its own Pusher echo.
        headers: editSessionUuid
          ? { 'edit-session-uuid': editSessionUuid }
          : undefined,
        method: 'PUT',
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/policy-diff/by-autoflow/${autoflowId}/save-progress`,
      }),
    }),
    updateAutoflowToolConfigurationFields: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: {
          configuration_fields:
            | (Record<string, unknown> & CustomEventToolConfigurationFields)
            | CustomEventCustomizationSettings
            | { parent_autoflow_intent_workflow_id: string }
            | {
                delay_seconds: number;
                interruptible?: boolean;
                play_hold_music?: boolean;
              };
          context_variable_output_mapping?: Record<string, string>;
          tool_id: string;
        };
        intentWorkflowId: string;
        version?: number;
        versionType?: string;
      }
    >({
      invalidatesTags: ['AutoflowVersionHistorySummary', 'AutoflowWorkflow'],
      query: ({ body, intentWorkflowId, version, versionType }) => ({
        body,
        method: 'PUT',
        params:
          version !== undefined || versionType
            ? {
                ...(version !== undefined && { version }),
                ...(versionType && { version_type: versionType }),
              }
            : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/tool/configuration-fields`,
      }),
    }),
    updatePolicyDescription: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: { policy_description: string };
        intentWorkflowId: string;
        version?: number;
        versionType?: string;
      }
    >({
      // Intentionally does NOT invalidate FlowDiagram. The Outline panel
      // tracks staleness locally via a "lastSyncedPolicy" snapshot and shows
      // an out-of-sync banner; the user clicks the banner's refresh button to
      // regenerate. Auto-invalidating here would burn an OpenAI call on every
      // policy save.
      invalidatesTags: ['AutoflowVersionHistorySummary', 'AutoflowWorkflow'],
      query: ({ body, intentWorkflowId, version, versionType }) => ({
        body,
        method: 'PUT',
        params:
          version !== undefined || versionType
            ? {
                ...(version !== undefined && { version }),
                ...(versionType && { version_type: versionType }),
              }
            : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/policy-description`,
      }),
    }),
    updateVersionMetadata: builder.mutation<
      AutoflowIntentWorkflowResponse,
      {
        body: { version_name: string };
        intentWorkflowId: string;
        version: number;
        versionType?: string;
      }
    >({
      invalidatesTags: ['AutoflowWorkflow', 'AutoflowVersionHistorySummary'],
      query: ({ body, intentWorkflowId, version, versionType }) => ({
        body,
        method: 'PATCH',
        params: versionType ? { version_type: versionType } : undefined,
        url:
          SOLVE_AUTOFLOW_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/versions/${version}/metadata`,
      }),
    }),
    uploadContextAttachment: builder.mutation<
      ContextAttachmentResponse,
      { file: File; intentWorkflowId: string }
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ file, intentWorkflowId }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          body: formData,
          method: 'POST',
          url:
            SOLVE_AUTOFLOW_BASE_URL +
            `/improved-policy/${intentWorkflowId}/context-attachments`,
        };
      },
    }),
  }),
});

export const {
  useAddAutoflowToolMutation,
  useChangeWorkflowPublishStatusMutation,
  useClonePrivateWorkflowsMutation,
  useCreateBlankSavedVersionMutation,
  useCreatePolicyDiffMutation,
  useCreateSavedVersionMutation,
  useCustomizeAutoflowTemplateMutation,
  useDeleteAutoflowToolMutation,
  useDeleteVersionMutation,
  useDiscardAutoflowWorkflowChangesMutation,
  useGenerateImprovedPolicyMutation,
  useGetAllAutoflowVersionHistoriesV2Query,
  useGetAutoflowTemplatesQuery,
  useGetAutoflowToolsQuery,
  useGetAutoflowVersionHistoryQuery,
  useGetAutoflowVersionHistoryV2Query,
  useGetAutoflowWorkflowQuery,
  useGetAutoflowWorkflowVersionQuery,
  useGetBuiltInToolSchemasQuery,
  useGetDeletedBuildingBlockQuery,
  useGetImprovedPolicyGenerationEligibilityQuery,
  useGetImprovedPolicyGenerationLatestJobQuery,
  useGetPolicyDiffByAutoflowQuery,
  useGetRestorationRequirementsBatchQuery,
  useGetRestorationRequirementsQuery,
  useLazyGetAutoflowVersionHistoryV2Query,
  useLazyGetRestorationRequirementsQuery,
  usePatchAutoflowCVsMutation,
  usePatchVersionHistoryMutation,
  usePublishLatestVersionMutation,
  usePublishWorkflowVersionMutation,
  useResetPolicyDiffMutation,
  useSavePolicyDiffProgressMutation,
  useUpdateAutoflowToolConfigurationFieldsMutation,
  useUpdatePolicyDescriptionMutation,
  useUpdateVersionMetadataMutation,
  useUploadContextAttachmentMutation,
} = workflowBuilderAutoflowApi;
